import { z } from "zod";
import { Address, MailLocation, Uid } from "./docs.js";
import { AccountId, ScopedId } from "./ids.js";
import { KindReason, MessageKind, RawHeader } from "./kinds.js";
import { GhGroup, MessageSource } from "./sources.js";
/**
 * The mail read api and mail actions (architecture §6.5, §9). Every id that leaves the api is
 * scoped, `{accountId}:{providerId}`; bodies are fetched live and never stored (§6.4).
 */
// ── read ──────────────────────────────────────────────────────────────────────────────────────
export const MailSearchInput = z.object({
    /** Gmail syntax, including the scope operators `account:` and `workspace:` (§6.5). */
    query: z.string().max(2000),
    workspaceId: z.string().min(1).optional(),
    accountIds: z.array(AccountId).min(1).optional(),
    limit: z.number().int().min(1).max(200).default(50),
    /** `nextCursor` of the previous page. */
    cursor: z.string().min(1).optional(),
});
export const MailHit = z.object({
    id: ScopedId,
    threadId: ScopedId,
    accountId: AccountId,
    accountAddress: z.string(),
    from: Address,
    to: z.array(Address),
    cc: z.array(Address),
    subject: z.string(),
    snippet: z.string(),
    date: z.date(),
    location: MailLocation,
    folderName: z.string().nullable(),
    tags: z.array(z.string()),
    unread: z.boolean(),
    starred: z.boolean(),
    hasAttachments: z.boolean(),
    /** What sync sorted it into (§6.7); null for mail not cached yet. */
    kind: MessageKind.nullable(),
    kindReason: KindReason.nullable(),
    /** What a known app says it is about (§6.7, WP-062), and its thread's GitHub group; null when none or not cached. */
    source: MessageSource.nullable(),
    ghGroup: GhGroup.nullable(),
});
/** One account that could not be searched; the search itself still succeeds. */
export const MailSearchWarning = z.object({
    accountId: AccountId,
    address: z.string(),
    reason: z.string(),
    message: z.string(),
});
export const MailSearchOutput = z.object({
    results: z.array(MailHit),
    warnings: z.array(MailSearchWarning),
    accountsSearched: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
});
export const MailGetThreadInput = z.object({ threadId: ScopedId });
export const MailGetRawInput = z.object({ messageId: ScopedId });
/**
 * "Show original" (§6.4): every header and provider field sync kept for one message. `capturedAt`
 * is null for mail cached before WP-061 whose raw fill has not reached it yet (no headers then).
 */
export const MailRawView = z.object({
    id: ScopedId,
    accountId: AccountId,
    headers: z.array(RawHeader),
    provider: z.record(z.string(), z.unknown()),
    capturedAt: z.date().nullable(),
    truncated: z.boolean(),
});
export const MailGetMessageInput = z.object({ messageId: ScopedId });
/** A message with its body as the provider returned it. Sanitizing HTML is the web's job. */
export const MailMessageView = MailHit.extend({
    internetMessageId: z.string().nullable(),
    /** `id` is what `GET /mail/attachments/{messageId}/{id}` takes; null when not downloadable. */
    attachments: z.array(z.object({ id: z.string().nullable(), name: z.string(), size: z.number(), mimeType: z.string(), inline: z.boolean() })),
    html: z.string().nullable(),
    text: z.string().nullable(),
});
export const MailThreadView = z.object({
    id: ScopedId,
    accountId: AccountId,
    accountAddress: z.string(),
    subject: z.string(),
    /** Oldest first. */
    messages: z.array(MailMessageView),
});
// ── actions ───────────────────────────────────────────────────────────────────────────────────
const Tags = z.array(z.string().min(1).max(100)).min(1).max(20);
/** What `mail.modify` can do to messages (capability `organize`). */
export const MailModifyOp = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("archive") }),
    z.object({ kind: z.literal("unarchive") }),
    z.object({ kind: z.literal("read") }),
    z.object({ kind: z.literal("unread") }),
    z.object({ kind: z.literal("star") }),
    z.object({ kind: z.literal("unstar") }),
    z.object({ kind: z.literal("spam") }),
    z.object({ kind: z.literal("notSpam") }),
    /** Back out of the trash (the Trash view's Restore, WP-056). */
    z.object({ kind: z.literal("untrash") }),
    z.object({ kind: z.literal("addTags"), tags: Tags }),
    z.object({ kind: z.literal("removeTags"), tags: Tags }),
    z.object({ kind: z.literal("moveToFolder"), folder: z.string().min(1).max(100) }),
]);
/** A plan's op: any modify op, or trash. */
export const MailPlanOp = z.union([MailModifyOp, z.object({ kind: z.literal("trash") })]);
/** Messages acted on without a plan: a thread or a small selection (§9 "single item"). */
export const DIRECT_LIMIT = 100;
/** The most messages one plan may hold. */
export const PLAN_LIMIT = 5000;
export const MailModifyInput = z.object({
    messageIds: z.array(ScopedId).min(1).max(DIRECT_LIMIT),
    op: MailModifyOp,
});
export const MailTrashInput = z.object({ messageIds: z.array(ScopedId).min(1).max(DIRECT_LIMIT) });
/**
 * A bulk change as a plan stores it: provider ids grouped per account (scoped ids would repeat the
 * account id 5,000 times inside a 1 MiB document).
 */
export const MailBulkPayload = z.object({
    op: MailPlanOp,
    accounts: z.array(z.object({ accountId: AccountId, messageIds: z.array(z.string().min(1)).min(1) })).min(1),
});
export const MailPlanBulkInput = z.object({
    query: z.string().max(2000),
    workspaceId: z.string().min(1).optional(),
    accountIds: z.array(AccountId).min(1).optional(),
    op: MailPlanOp,
});
/**
 * A plan over messages picked one by one (the inbox's bulk selection) rather than a query: used
 * when the selection is too big for `mail.modify` or is spam/trash over several conversations.
 */
export const MailPlanSelectionInput = z.object({
    messageIds: z.array(ScopedId).min(1).max(PLAN_LIMIT),
    op: MailPlanOp,
});
export const MailPlanSample = z.object({ from: z.string(), subject: z.string(), date: z.date(), account: z.string() });
export const MailPlanBulkOutput = z.object({
    /** null when nothing matched: there is nothing to apply. */
    planId: z.string().nullable(),
    action: z.string(),
    capability: z.enum(["organize", "destructive"]),
    matched: z.number().int().nonnegative(),
    sample: z.array(MailPlanSample),
    summary: z.string(),
    warnings: z.array(MailSearchWarning),
    expiresAt: z.date().nullable(),
});
/** What a modify, trash or bulk action returns. */
export const MailChangeResult = z.object({
    changed: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    failed: z.array(z.object({ id: z.string(), reason: z.string() })),
});
const Recipient = z.object({ name: z.string().nullable().default(null), email: z.string().email() });
export const MailSendInput = z
    .object({
    accountId: AccountId,
    to: z.array(Recipient).min(1).max(100),
    cc: z.array(Recipient).max(100).default([]),
    bcc: z.array(Recipient).max(100).default([]),
    subject: z.string().max(998),
    text: z.string().max(500_000).nullable().default(null),
    html: z.string().max(2_000_000).nullable().default(null),
    /** The message this replies to; it must belong to `accountId`. */
    inReplyTo: ScopedId.nullable().default(null),
})
    .refine((d) => d.text !== null || d.html !== null, { message: "a draft needs a text or html body" });
export const MailForwardInput = z.object({
    messageId: ScopedId,
    to: z.array(Recipient).min(1).max(100),
    note: z.string().max(100_000).default(""),
});
export const MailSentResult = z.object({ accountId: AccountId, messageId: ScopedId.nullable(), threadId: ScopedId.nullable() });
/** The furthest ahead a snooze may wake (a year), and the nearest (a minute). */
export const SNOOZE_MAX_MS = 366 * 24 * 3600 * 1000;
export const SNOOZE_MIN_MS = 60 * 1000;
/**
 * `mail.snooze` (capability `organize`, undoable): archive the messages now and bring them back to
 * the inbox, unread, at `until` (the sync service's `/cron/unsnooze`). One `snoozes/{id}` per thread.
 */
export const MailSnoozeInput = z.object({
    ids: z.array(ScopedId).min(1).max(DIRECT_LIMIT),
    until: z.coerce.date(),
});
export const MailSnoozeResult = MailChangeResult.extend({
    snoozeIds: z.array(z.string()),
    until: z.date(),
});
/**
 * `mail.unsnooze` (capability `organize`, undoable): wake snoozed conversations now instead of at
 * their time. `ids` are scoped message ids (or scoped thread ids); every snooze of the caller that
 * holds one of them wakes exactly as `/cron/unsnooze` would wake it.
 */
export const MailUnsnoozeInput = z.object({ ids: z.array(ScopedId).min(1).max(DIRECT_LIMIT) });
/**
 * `changed`: messages brought back; `unchanged`: messages of a woken snooze that had been moved
 * elsewhere and stayed there; `failed`: ids that are not snoozed (`not_snoozed`) or whose snooze
 * could not wake (`needs_reconnect`, or the provider's reason).
 */
export const MailUnsnoozeResult = MailChangeResult.extend({ snoozeIds: z.array(z.string()) });
export const MailBlockSenderInput = z.object({
    accountId: AccountId,
    sender: z.string().email(),
    action: z.enum(["spam", "trash"]).default("spam"),
});
// ── stored documents ──────────────────────────────────────────────────────────────────────────
/** The organizable fields a mutation is expected to leave a message with (§6.3 echo suppression). */
export const ExpectedFields = z.object({
    location: MailLocation.optional(),
    folderName: z.string().nullable().optional(),
    tags: z.array(z.string()).optional(),
    unread: z.boolean().optional(),
    starred: z.boolean().optional(),
});
/**
 * One expectation: these provider message ids of this account should, after our own mutation,
 * show exactly `expected` in the fields it names. A notification for such a message whose state
 * matches is our echo: the sync worker applies it to the cache but does not re-run rules on it.
 */
export const ExpectedState = z.object({
    ownerUid: Uid,
    accountId: AccountId,
    messageIds: z.array(z.string().min(1)),
    expected: ExpectedFields,
    expiresAt: z.date(),
});
/**
 * `operations/{requestId}`: written by the action runtime for idempotency (`outcome`), and by mail
 * actions before they call the provider (`expectedState`, one entry per account and distinct
 * expected state; `accountIds` lets the sync worker query by account).
 */
export const OperationDoc = z.object({
    ownerUid: Uid,
    action: z.string(),
    expectedState: z.array(ExpectedState).default([]),
    accountIds: z.array(AccountId).default([]),
    expiresAt: z.date().nullable().default(null),
    outcome: z.unknown().default(null),
    createdAt: z.date(),
});
/**
 * `snoozes/{id}`: a snoozed thread waiting for its time (written by `mail.snooze`, deleted by the
 * sync service's `/cron/unsnooze` or by `mail.unsnooze` once the messages are back in the inbox,
 * or by undo).
 * `messageIds` are provider ids: the messages the snooze archived (only those come back).
 */
export const SnoozeDoc = z.object({
    ownerUid: Uid,
    accountId: AccountId,
    threadId: z.string().min(1),
    messageIds: z.array(z.string().min(1)).min(1),
    until: z.date(),
    requestId: z.string(),
    createdAt: z.date(),
});
//# sourceMappingURL=mail-api.js.map